import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { LoadingSpinner } from '../../ui/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import { NumberedPagination, useTimezone } from '@kioskgaming/ui';
import { useAdminAgentWalletTransactions } from '../../../hooks/useApi';
import { usePermission } from '../../../hooks/useAuth';
import type { AdminAgentWalletTransactionRow } from '../../../types';
import {
  AGENT_TX_PAGE_SIZE,
  formatAgentTxDate,
  formatWalletLedgerType,
  formatCdnLedgerAmount,
  formatReferenceCell,
  getSignedLedgerDelta,
} from './agentCdnTxUtils';

export type AgentWalletTransferPanelProps = {
  agentId: string;
  queryEnabled?: boolean;
};

export const AgentWalletTransferPanel: React.FC<AgentWalletTransferPanelProps> = ({ agentId, queryEnabled = true }) => {
  const { timezone } = useTimezone();
  const canManage = usePermission('agentManagement');
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    setPage(1);
  }, [typeFilter]);

  const params = useMemo(
    () => ({
      page,
      limit: AGENT_TX_PAGE_SIZE,
      ...(typeFilter ? { type: typeFilter } : {}),
    }),
    [page, typeFilter],
  );

  const fetchEnabled = !!agentId && canManage && queryEnabled;
  const query = useAdminAgentWalletTransactions(agentId, fetchEnabled, params);

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Wallet transfer</CardTitle>
          <p className="text-xs text-muted-foreground font-normal mt-1">CDN wallet ledger entries for this agent.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-muted-foreground whitespace-nowrap" htmlFor={`wallet-type-filter-${agentId}`}>
            Type
          </label>
          <select
            id={`wallet-type-filter-${agentId}`}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="deposit">Deposit</option>
            <option value="withdrawal">Withdrawal</option>
            <option value="transfer">Transfer</option>
            <option value="bonus">Bonus</option>
            <option value="adjustment">Balance correction</option>
            <option value="reservation">Reservation</option>
            <option value="reservation_release">Reservation release</option>
            <option value="credit">Credit</option>
            <option value="debit">Debit</option>
          </select>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {query.isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : query.error ? (
          <p className="px-6 py-8 text-sm text-destructive">Failed to load wallet ledger.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Time</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance after</TableHead>
                    <TableHead>Reference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(query.data?.data?.transactions ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                        No ledger entries yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (query.data?.data?.transactions ?? []).map((row: AdminAgentWalletTransactionRow) => {
                      const { signed, displayAbs } = getSignedLedgerDelta(row);
                      const refCell = formatReferenceCell(row);
                      const signPrefix = signed > 0 ? '+' : signed < 0 ? '−' : '';
                      const amountClass =
                        signed > 0 ? 'text-green-700' : signed < 0 ? 'text-red-700' : 'text-muted-foreground';
                      return (
                        <TableRow key={row.id}>
                          <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                            {formatAgentTxDate(row.createdAt, timezone)}
                          </TableCell>
                          <TableCell className="text-sm">{formatWalletLedgerType(row.type)}</TableCell>
                          <TableCell className={`text-right tabular-nums text-sm font-medium ${amountClass}`}>
                            {displayAbs === '—' ? '—' : `${signPrefix}${displayAbs}`}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm text-foreground">
                            {formatCdnLedgerAmount(row.balanceAfter)}
                          </TableCell>
                          <TableCell className="text-sm max-w-[14rem]">
                            {refCell ? (
                              <span className="font-mono text-xs break-all" title={refCell.title}>
                                {refCell.text}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3 text-sm text-muted-foreground">
              <span>
                Page {query.data?.data?.pagination.page ?? 1} of {query.data?.data?.pagination.totalPages ?? 1} (
                {query.data?.data?.pagination.total ?? 0} total)
              </span>
              <NumberedPagination
                currentPage={page}
                totalPages={query.data?.data?.pagination.totalPages ?? 1}
                onPageChange={setPage}
                disabled={query.isFetching}
                variant="admin"
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
